<template>
  <div class="px-2">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9E6D2B"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#9E6D2B" right :timeout="timeout">
      <span style="color: #FFFFFF; font-weight: 600;">{{ msg }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="showsnackbar = false">
          <v-icon style="color: #FFFFFF">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- TOP HEADER & CONTROLS -->
    <v-row align="center" justify="space-between" class="mb-3">
      <v-col cols="12" sm="6" md="6" class="py-1">
        <span class="mainHeader">Daily Stock Report</span>
      </v-col>
      <v-col cols="12" sm="6" md="6" class="d-flex align-center justify-end py-1" style="gap: 12px;">
        <!-- As of Date Picker -->
        <div style="width: 170px;">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            :return-value.sync="date"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="date"
                label="As of Date"
                outlined
                dense
                readonly
                hide-details
                background-color="#FFFFFF"
                v-bind="attrs"
                v-on="on"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="date"
              no-title
              scrollable
              color="#9E6D2B"
              @change="$refs.menu.save(date)"
            >
              <v-spacer></v-spacer>
            </v-date-picker>
          </v-menu>
        </div>

        <!-- Download PDF Button -->
        <v-btn
          color="#9E6D2B"
          dark
          height="40"
          elevation="1"
          class="text-none font-weight-bold px-4"
          style="border-radius: 8px;"
          @click="download"
          :disabled="appLoading"
        >
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Download PDF
        </v-btn>
      </v-col>
    </v-row>

    <!-- ITEM WISE STOCK CARD -->
    <v-card outlined class="rounded-lg overflow-hidden elevation-1 mb-5" color="#FFFFFF">
      <div class="section-title-bar px-4 py-2.5 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon small color="#9E6D2B" class="mr-2">mdi-format-list-bulleted-type</v-icon>
          <span class="font-weight-bold text-subtitle-2">ITEM WISE STOCK</span>
        </div>
        <span class="subtext-count" v-if="itemRows.length > 0">{{ itemRows.length }} Stock Lots</span>
      </div>

      <div class="table-container">
        <table class="report-table">
          <thead>
            <tr>
              <th class="text-left">DESIGN CODE</th>
              <th class="text-left">DESIGN NAME</th>
              <th class="text-left">SUPPLIER</th>
              <th class="text-center">METAL</th>
              <th class="text-center">TYPE</th>
              <th class="text-center">QTY</th>
              <th class="text-right">NET WEIGHT (g)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="itemRows.length === 0">
              <td colspan="7" class="text-center text-muted py-6">No item stock data available as of this date.</td>
            </tr>
            <tr v-for="(row, rIdx) in itemRows" :key="rIdx" class="table-row">
              <td class="text-left font-weight-semibold">
                <span class="code-badge">{{ row.designCode || '-' }}</span>
              </td>
              <td class="text-left font-weight-medium">{{ row.designName || '-' }}</td>
              <td class="text-left text-muted">{{ row.supplierName || '-' }}</td>
              <td class="text-center">
                <span class="metal-pill" :class="getMetalClass(row.metalType)">{{ row.metalType || 'Gold' }}</span>
              </td>
              <td class="text-center">
                <span class="type-pill" :class="'type-pill--' + (row.lotType || 'count').toLowerCase()">{{ row.lotType || 'Count' }}</span>
              </td>
              <td class="text-center font-weight-bold">{{ row.lotType === 'Count' ? (row.qty || 0) : '—' }}</td>
              <td class="text-right font-weight-bold gold-weight-text">
                {{ (row.netWeight || 0).toFixed(3) }}
              </td>
            </tr>
          </tbody>
          <tfoot v-if="itemRows.length > 0">
            <tr class="totals-row">
              <td colspan="5" class="text-left total-label-cell">TOTAL ITEM STOCK</td>
              <td class="text-center"><strong>{{ itemTotals.qty }}</strong></td>
              <td class="text-right gold-total-cell"><strong>{{ itemTotals.weight.toFixed(3) }}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </v-card>

    <!-- DESIGN WISE SUMMARY CARD -->
    <v-card outlined class="rounded-lg overflow-hidden elevation-1 mb-5" color="#FFFFFF">
      <div class="section-title-bar px-4 py-2.5 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon small color="#9E6D2B" class="mr-2">mdi-rhombus-split</v-icon>
          <span class="font-weight-bold text-subtitle-2">DESIGN WISE SUMMARY</span>
        </div>
        <span class="subtext-count" v-if="designRows.length > 0">{{ designRows.length }} Designs</span>
      </div>

      <div class="table-container">
        <table class="report-table">
          <thead>
            <tr>
              <th class="text-left">DESIGN CODE</th>
              <th class="text-left">DESIGN NAME</th>
              <th class="text-center">TOTAL QTY</th>
              <th class="text-right">TOTAL NET WEIGHT (g)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="designRows.length === 0">
              <td colspan="4" class="text-center text-muted py-6">No design stock summary available as of this date.</td>
            </tr>
            <tr v-for="(row, dIdx) in designRows" :key="dIdx" class="table-row">
              <td class="text-left font-weight-semibold">
                <span class="code-badge">{{ row.designCode || '-' }}</span>
              </td>
              <td class="text-left font-weight-medium">{{ row.designName || '-' }}</td>
              <td class="text-center font-weight-bold">{{ row.qty || 0 }}</td>
              <td class="text-right font-weight-bold gold-weight-text">
                {{ (row.weight || 0).toFixed(3) }}
              </td>
            </tr>
          </tbody>
          <tfoot v-if="designRows.length > 0">
            <tr class="totals-row">
              <td colspan="2" class="text-left total-label-cell">TOTAL DESIGN SUMMARY</td>
              <td class="text-center"><strong>{{ designTotals.qty }}</strong></td>
              <td class="text-right gold-total-cell"><strong>{{ designTotals.weight.toFixed(3) }}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "DailyStockReportPage",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 3000,
      msg: null,
      menu: false,
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      itemRows: [],
      designRows: [],
      itemTotals: { qty: 0, weight: 0 },
      designTotals: { qty: 0, weight: 0 },
    };
  },
  watch: {
    date() {
      this.getList();
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    getMetalClass(metal) {
      const m = (metal || "").toLowerCase();
      if (m.includes("silver")) return "metal-silver";
      if (m.includes("plat")) return "metal-platinum";
      if (m.includes("diamond")) return "metal-diamond";
      return "metal-gold";
    },
    getList() {
      if (!this.date) return;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/data/dailystockreport",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          asOf: this.date,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status) {
            this.itemRows = response.data.data.itemRows || [];
            this.designRows = response.data.data.designRows || [];
            this.itemTotals = response.data.data.itemTotals || { qty: 0, weight: 0 };
            this.designTotals = response.data.data.designTotals || { qty: 0, weight: 0 };
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to fetch stock report";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    download() {
      if (!this.date) {
        this.msg = "Please choose a date";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/dailystockreport",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          asOf: this.date,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `DailyStockReport_${this.date}.pdf`);
          document.body.appendChild(link);
          link.click();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

.mainHeader {
  font-family: 'Cinzel', serif;
  font-size: 22px;
  font-weight: 700;
  color: #1E1815;
  letter-spacing: 0.3px;
}

.section-title-bar {
  background: #FAF6EF;
  color: #1E1815;
  border-bottom: 1px solid #EFE8DD;
}

.subtext-count {
  font-size: 11.5px;
  color: #8C7D6F;
  font-weight: 600;
}

.table-container {
  overflow-x: auto;
  width: 100%;
}

.report-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.report-table th {
  background: #FAF8F5;
  color: #6B5738;
  font-size: 11.5px;
  font-weight: 700;
  padding: 10px 14px;
  vertical-align: middle;
  border-bottom: 1px solid #EFE8DD;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.report-table td {
  padding: 9px 14px;
  vertical-align: middle;
  border-bottom: 1px solid #F3EFE8;
  white-space: nowrap;
  color: #1E1815;
}

.table-row:hover td {
  background-color: #FAF5EA !important;
}

.code-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: #F4ECE0;
  color: #7D551E;
  font-size: 12px;
  font-family: monospace;
}

.metal-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11.5px;
  font-weight: 600;
}

.metal-gold { background: #FEF3C7; color: #B45309; }
.metal-silver { background: #F3F4F6; color: #4B5563; }
.metal-platinum { background: #F0F9FF; color: #0369A1; }
.metal-diamond { background: #EFF6FF; color: #1D4ED8; }

.type-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.type-pill--count { color: #1d4ed8; background: #e8f0fe; }
.type-pill--split { color: #15803d; background: #e5f6ec; }
.type-pill--bulk { color: #9a6b27; background: #f5ecdc; }

.gold-weight-text {
  color: #9E6D2B;
}

.text-muted {
  color: #8C7D6F;
}

.totals-row td {
  background-color: #F7F2E8 !important;
  border-top: 2px solid #9E6D2B !important;
  border-bottom: none !important;
  color: #1E1815;
  font-weight: 700;
  padding: 11px 14px;
}

.total-label-cell {
  color: #9E6D2B !important;
  font-family: 'Cinzel', serif;
  font-size: 13px !important;
}

.gold-total-cell {
  color: #9E6D2B !important;
  font-size: 14px;
}
</style>
